/**
 * The picker for a File column cell: the files already in the cell, an "Add
 * files" button that uploads through lib/files, and the account's allowance.
 *
 * Removing a chip only takes the file out of this cell. The server releases
 * the stored file when no template uses it any more (on save), so a file that
 * sits on several rows, or in a duplicated template, is never pulled from
 * under them.
 *
 * Draft value: { files: [{ fileId, name, size }] } (lib/columnValues).
 */
import { Attach, CloseSmall, Download } from '@vibe/icons';
import React, { useEffect, useRef } from 'react';
import Spinner from '../Spinner';
import { ACCEPT_ATTRIBUTE, FILES_PER_CELL, formatBytes, supportsFiles } from '../../lib/fileLimits';

/**
 * Uploads belong to the editor (lib/cellUploads), not to this popup: closing it
 * leaves them running, the table cell shows their progress, and a finished one
 * lands in the cell through the editor. This popup starts them, lists the
 * cell's, and can cancel one.
 *
 * files    lib/files service (downloads)
 * uploads  the editor's useCellUploads store
 * cell     this cell, as the editor describes it to the store
 */
export default function FileCellEditor({ files: service, uploads: store, cell, value, onChange }) {
  const list = (value && value.files) || [];
  const uploads = store.forCell(cell);
  const { usage, usageError, loadUsage } = store;
  const inputRef = useRef(null);

  // the allowance, read again each time the popup opens
  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const limits = usage && usage.limits;
  const planBlocks = Boolean(usage && usage.plan && !usage.plan.supportsFiles) || Boolean(limits && !supportsFiles(limits));
  const slotsLeft = FILES_PER_CELL - list.length - uploads.filter((u) => !u.error).length;
  const canAdd = !planBlocks && slotsLeft > 0;

  const startUploads = (picked) => {
    const chosen = Array.from(picked || []).slice(0, Math.max(0, slotsLeft));
    if (chosen.length) store.start(cell, chosen);
  };

  const remove = (fileId) => {
    const next = list.filter((f) => f.fileId !== fileId);
    onChange(next.length ? { files: next } : null);
  };

  const download = (fileId) => {
    service
      .downloadUrl(fileId)
      .then((url) => window.open(url, '_blank', 'noopener'))
      .catch(() => undefined);
  };

  return (
    <div className="spt-files" data-testid="file-cell-editor">
      <div className="spt-menu-caption">Files</div>
      {list.length === 0 && uploads.length === 0 && <div className="spt-menu-empty">No files yet.</div>}
      <ul className="spt-files-list" aria-label="Files in this cell">
        {list.map((f) => (
          <li key={f.fileId} className="spt-filechip">
            <Attach className="spt-filechip-icon" aria-hidden="true" />
            <span className="spt-filechip-name" title={f.name}>
              {f.name}
            </span>
            <span className="spt-filechip-size">{formatBytes(f.size)}</span>
            <button type="button" className="spt-filechip-btn" aria-label={`Download ${f.name}`} title="Download" onClick={() => download(f.fileId)}>
              <Download aria-hidden="true" />
            </button>
            <button type="button" className="spt-filechip-btn" aria-label={`Remove ${f.name}`} title="Remove from this cell" onClick={() => remove(f.fileId)}>
              <CloseSmall aria-hidden="true" />
            </button>
          </li>
        ))}
        {uploads.map((u) => (
          <li key={u.key} className={`spt-filechip ${u.error ? 'spt-filechip-error' : 'spt-filechip-uploading'}`} role={u.error ? 'alert' : 'status'}>
            {u.error ? <Attach className="spt-filechip-icon" aria-hidden="true" /> : <Spinner size={14} className="spt-filechip-icon" />}
            <span className="spt-filechip-name" title={u.name}>
              {u.name}
            </span>
            <span className="spt-filechip-size">{u.error ? u.error : `${u.pct}%`}</span>
            <button
              type="button"
              className="spt-filechip-btn"
              aria-label={u.error ? `Dismiss ${u.name}` : `Cancel uploading ${u.name}`}
              title={u.error ? 'Dismiss' : 'Cancel upload'}
              onClick={() => store.cancel(u.key)}
            >
              <CloseSmall aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        className="hidden"
        data-testid="file-cell-input"
        onChange={(e) => {
          startUploads(e.target.files);
          e.target.value = '';
        }}
      />
      <button type="button" className="spt-files-add" disabled={!canAdd} onClick={() => inputRef.current && inputRef.current.click()}>
        Add files
      </button>

      <div className="spt-files-foot">
        {usageError && <span>Could not read your file allowance.</span>}
        {!usageError && !usage && (
          <span className="inline-flex items-center gap-1">
            <Spinner size={12} /> Checking your plan
          </span>
        )}
        {usage && planBlocks && <span>File columns are not included in your plan.</span>}
        {usage && !planBlocks && limits && (
          <span>
            {usage.files} of {limits.filesStored} files · {formatBytes(usage.bytes)} of {formatBytes(limits.bytesStored)} · up to {FILES_PER_CELL} per cell
          </span>
        )}
        {usage && !planBlocks && !limits && <span>Up to {FILES_PER_CELL} files per cell.</span>}
      </div>
    </div>
  );
}
